import { MessageResponse } from "@/components/ai-elements/message";
import { InlineMarkdownEditor } from "@/components/agent-editor/inline-markdown-editor";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FilePill, type FilePillProps } from "@/components/ui/file-pill";
import { isViewableFileName, openFileBlob, openFileUrl } from "@/lib/file-links";
import { cn } from "@/lib/utils";
import { formatTaskDueDate, isCalendarDate, isTaskOverdue, todayInTimezone } from "@/lib/task-due-date";
import type { TaskStatus } from "@/lib/project-types";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { tagColorClass, tagColorStyle } from "@/components/kanban/KanbanCard";
import type { Tag, TenantUser } from "@/lib/project-types";
import { Check, ChevronDown, FileText, Loader2, Paperclip, Pencil, Plus, Tag as TagIcon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

const TAG_COLOR_OPTIONS = ["gray", "red", "orange", "yellow", "green", "blue", "purple", "pink"] as const;

interface TaskBriefCardProps {
  taskTitle?: string;
  onSaveTitle?: (title: string) => Promise<boolean>;
  /** Nullable task reference material returned by the Projects API. */
  description: string | null | undefined;
  /** Collapse the body after a conversation has messages, while keeping it pinned. */
  collapsed: boolean;
  /** Available only where the task's Projects API update path is wired. */
  onSaveDescription?: (description: string) => Promise<boolean>;
  dueDate?: string | null;
  dueDateSupported?: boolean;
  taskStatus?: TaskStatus;
  timezone?: string;
  onSaveDueDate?: (dueDate: string | null) => Promise<boolean>;
  assignedTo?: string | null;
  tenantUsers?: TenantUser[];
  onSaveAssignee?: (assigneeId: string | null) => Promise<boolean>;
  onAssigneePickerOpen?: () => void;
  tags?: Tag[];
  effectiveTags?: { tag: Tag; inherited: boolean; source_task: { id: string; title: string } | null }[];
  allTags?: Tag[];
  onSaveTags?: (tagIds: string[]) => Promise<boolean>;
  onCreateTag?: (name: string, color?: string) => Promise<Tag | null>;
  tagLoading?: boolean;
  tagLoadError?: string | null;
  tagCreateError?: string | null;
  tagSupported?: boolean;
  artifacts?: Record<string, unknown>[];
  onRemoveArtifact?: (path: string) => Promise<boolean>;
  getArtifactDownloadLink?: (path: string) => Promise<{ url: string; filename: string; media_type: string | null } | null>;
  fetchArtifactRaw?: (path: string) => Promise<Blob | null>;
  onOpenFile?: (url: string, filename: string) => void;
  /** Independent controls appended to the compact detail bar. */
  headerActions?: ReactNode;
  className?: string;
}

function readArtifact(value: Record<string, unknown>) {
  if (typeof value.path !== "string" || !value.path) return null;
  const filename = typeof value.filename === "string" && value.filename.trim()
    ? value.filename : value.path.split(/[\\/]/).at(-1) || value.path;
  return {
    path: value.path,
    // Registration stores a human label, not a filename. Keep the path's
    // extension for viewer detection and raw downloads, regardless of label.
    filename,
    displayName: typeof value.filename === "string" && value.filename.trim()
      ? value.filename : typeof value.label === "string" && value.label.trim()
        ? value.label : filename,
    mediaType: typeof value.media_type === "string" ? value.media_type
      : typeof value.type === "string" ? value.type : null,
  };
}

/**
 * Dedicated task context, intentionally distinct from a user chat bubble.
 *
 * Markdown goes through the app's shared MessageResponse/Streamdown renderer,
 * including its CJK, code, math, Mermaid, and link-safety configuration.
 */
export function TaskBriefCard({ taskTitle, onSaveTitle, description, collapsed, onSaveDescription, dueDate, dueDateSupported = false,
  taskStatus = "open", timezone = "Europe/Zurich", onSaveDueDate, assignedTo, tenantUsers = [], onSaveAssignee, onAssigneePickerOpen,
  tags, effectiveTags, allTags = [], onSaveTags, onCreateTag, tagLoading = false, tagLoadError = null, tagCreateError = null, tagSupported = true,
  artifacts, onRemoveArtifact,
  getArtifactDownloadLink, fetchArtifactRaw, onOpenFile, headerActions, className }: TaskBriefCardProps) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(!collapsed);
  // Respect the parent's auto-collapse when messages arrive, while allowing
  // the user to toggle either state between those transitions.
  useEffect(() => setExpanded(!collapsed), [collapsed]);
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draft, setDraft] = useState("");
  const [saveError, setSaveError] = useState(false);
  const [draftDueDate, setDraftDueDate] = useState("");
  const [draftAssignee, setDraftAssignee] = useState("");
  const [draftTagIds, setDraftTagIds] = useState<string[]>([]);
  const [createdTags, setCreatedTags] = useState<Tag[]>([]);
  const [tagPopoverOpen, setTagPopoverOpen] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState<string | undefined>();
  const [creatingTag, setCreatingTag] = useState(false);
  const [localTagCreateError, setLocalTagCreateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [removingPath, setRemovingPath] = useState<string | null>(null);
  const [openingPath, setOpeningPath] = useState<string | null>(null);
  const visibleArtifacts = (artifacts ?? []).map(readArtifact).filter((item) => item !== null);
  const availableTags = [...allTags, ...createdTags.filter((tag) => !allTags.some((item) => item.id === tag.id))];
  const selectableTags = availableTags.filter((tag) => !tag.is_archived || draftTagIds.includes(tag.id));
  const tagPickerMessage = tagLoadError
    ? t(/^HTTP (404|405)\b/.test(tagLoadError) ? "projects:tagsUnavailable" : "projects:tagLoadFailed")
    : tagLoading ? t("projects:loadingTags")
      : selectableTags.length === 0 ? t("projects:noTagsYet") : null;
  const creationError = tagCreateError ?? localTagCreateError;
  const tagCreationUnavailable = Boolean(
    (tagLoadError && /^HTTP (404|405)\b/.test(tagLoadError))
    || (creationError && /^HTTP (403|404|405)\b/.test(creationError)),
  );
  const tagCreateMessage = creationError
    ? t(/^HTTP (404|405)\b/.test(creationError) ? "projects:tagCreationUnavailable"
      : /^HTTP 403\b/.test(creationError) ? "projects:tagCreationForbidden" : "projects:tagCreateFailed")
    : null;
  // Check for an empty brief without trimming the rendered value: leading
  // whitespace can be meaningful Markdown (notably an indented code block).
  const hasDescription = Boolean(description?.trim());
  // A lightweight excerpt only; the full, untouched Markdown is rendered below.
  const preview = (description ?? "")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^\s*(?:#{1,6}\s+|>\s*|[-*+]\s+|\d+\.\s+)/gm, "")
    .replace(/[*`~]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  const startEditing = () => {
    setDraftTitle(taskTitle ?? "");
    setDraft(description ?? "");
    setDraftDueDate(dueDate ?? "");
    setDraftAssignee(assignedTo ?? "");
    setDraftTagIds((tags ?? []).map((tag) => tag.id));
    setCreatedTags([]);
    setTagPopoverOpen(false);
    setNewTagName("");
    setNewTagColor(undefined);
    setLocalTagCreateError(null);
    setSaveError(false);
    setEditing(true);
    // Expand so the editor replaces only the body: the card header (trigger,
    // pills, count, edit affordance) stays in place while editing.
    setExpanded(true);
  };

  const cancelEditing = () => {
    setSaveError(false);
    setEditing(false);
  };

  const createTag = async () => {
    const name = newTagName.trim();
    if (!name || !onCreateTag || creatingTag || tagCreationUnavailable) return;
    setCreatingTag(true);
    setLocalTagCreateError(null);
    try {
      const tag = await onCreateTag(name, newTagColor);
      if (tag) {
        setCreatedTags((current) => [...current.filter((item) => item.id !== tag.id), tag]);
        setDraftTagIds((current) => current.includes(tag.id) ? current : [...current, tag.id]);
        setNewTagName("");
        setNewTagColor(undefined);
      } else {
        setLocalTagCreateError("failed");
      }
    } catch (error) {
      setLocalTagCreateError(error instanceof Error ? error.message : "failed");
    } finally {
      setCreatingTag(false);
    }
  };

  const saveTask = async () => {
    if ((!onSaveTitle && !onSaveDescription && !onSaveDueDate && !onSaveAssignee && !onSaveTags) || saving || creatingTag) return;
    if (onSaveTitle && !draftTitle.trim()) return;
    setSaving(true);
    setSaveError(false);
    try {
      const titleSaved = !onSaveTitle || draftTitle.trim() === taskTitle || await onSaveTitle(draftTitle.trim());
      const descriptionSaved = !onSaveDescription || draft === (description ?? "") || await onSaveDescription(draft);
      const dateSaved = !dueDateSupported || !onSaveDueDate || (draftDueDate || null) === (dueDate ?? null)
        || await onSaveDueDate(draftDueDate || null);
      const assigneeSaved = !onSaveAssignee || (draftAssignee || null) === (assignedTo ?? null)
        || await onSaveAssignee(draftAssignee || null);
      const currentTagIds = (tags ?? []).map((tag) => tag.id);
      const tagsSaved = !onSaveTags || (draftTagIds.length === currentTagIds.length
        && draftTagIds.every((id) => currentTagIds.includes(id))) || await onSaveTags(draftTagIds);
      if (titleSaved && descriptionSaved && dateSaved && assigneeSaved && tagsSaved) setEditing(false);
      else setSaveError(true);
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  const removeArtifact = async (path: string) => {
    if (!onRemoveArtifact || removingPath) return;
    setRemovingPath(path);
    try { await onRemoveArtifact(path); } finally { setRemovingPath(null); }
  };

  const openArtifact = async (artifact: NonNullable<ReturnType<typeof readArtifact>>) => {
    if (!getArtifactDownloadLink || openingPath) return;
    setOpeningPath(artifact.path);
    try {
      const preferSignedLink = onOpenFile && isViewableFileName(artifact.filename, artifact.mediaType);
      // Signed URLs preserve the conversation viewer's collaborative editor and file identity.
      // Older agno-server versions return 404 for raw but support download-links;
      // raw fallback covers mint failures or rejected paths on newer servers.
      if (preferSignedLink) {
        try {
          const link = await getArtifactDownloadLink(artifact.path);
          if (link) {
            openFileUrl(link.url, link.filename, onOpenFile);
            return;
          }
        } catch { /* Try raw bytes when signed-link minting fails. */ }
      }
      try {
        const blob = await fetchArtifactRaw?.(artifact.path);
        if (blob) {
          openFileBlob(blob, artifact.filename, artifact.mediaType, onOpenFile);
          return;
        }
      } catch { /* Downloads can still use signed links when raw is unavailable. */ }
      if (!preferSignedLink) {
        const link = await getArtifactDownloadLink(artifact.path);
        if (link) openFileUrl(link.url, link.filename, onOpenFile);
      }
    } finally { setOpeningPath(null); }
  };

  const artifactPill = (
    artifact: NonNullable<ReturnType<typeof readArtifact>>,
    removal: Pick<FilePillProps, "onRemove" | "removeAriaLabel" | "removing"> = {},
  ) => (
    <FilePill key={artifact.path} filename={artifact.displayName} mediaType={artifact.mediaType}
      className="min-w-0"
      onOpen={getArtifactDownloadLink ? () => void openArtifact(artifact) : undefined}
      openAction={getArtifactDownloadLink
        ? onOpenFile && isViewableFileName(artifact.filename, artifact.mediaType) ? "view" : "download"
        : undefined}
      opening={openingPath === artifact.path}
      {...removal} />
  );

  const artifactSection = visibleArtifacts.length > 0 ? (
    <div className="mt-3" aria-label={t("taskBrief.artifacts")}>
      <div className="flex flex-wrap gap-1.5">
        {visibleArtifacts.map((artifact) => artifactPill(artifact, {
          onRemove: editing && onRemoveArtifact ? () => void removeArtifact(artifact.path) : undefined,
          removeAriaLabel: t("taskBrief.removeArtifact", { filename: artifact.displayName }),
          removing: removingPath === artifact.path,
        }))}
      </div>
    </div>
  ) : null;

  // Cap the expanded brief at 60% of the dynamic viewport height and make it
  // an independent scroll container so a long draft never overflows over the
  // chat input. The collapsed pin stays compact: its body only scrolls when
  // the user opens it. Editing swaps only this body content (markdown for the
  // editor), keeping the surrounding card chrome stable.
  const body = (
    <div className="max-h-[60dvh] min-w-0 overflow-y-auto px-4 pb-4 pt-1 text-sm text-foreground">
      {editing ? (
        <>
          {onSaveTitle && (
            <label className="mb-3 grid gap-1.5 text-sm font-medium text-muted-foreground">
              {t("projects:taskTitlePlaceholder")}
              <Input value={draftTitle} onChange={(event) => setDraftTitle(event.target.value)}
                maxLength={500} required disabled={saving} aria-label={t("projects:taskTitlePlaceholder")} />
            </label>
          )}
          <InlineMarkdownEditor
            value={draft}
            onChange={setDraft}
            disabled={saving || !onSaveDescription}
            aria-label={t("taskBrief.editor")}
            contentClassName="min-h-24 max-h-[40dvh] overflow-y-auto"
          />
          {(dueDateSupported && onSaveDueDate || onSaveAssignee) && (
            <div className={cn("mt-3 grid gap-3", dueDateSupported && onSaveDueDate && onSaveAssignee && "sm:grid-cols-2")}>
              {dueDateSupported && onSaveDueDate && (
                <label className="min-w-0 grid gap-1.5 text-sm font-medium text-muted-foreground">
                  {t("projects:dueDate")}
                  <Input type="date" value={draftDueDate} onChange={(event) => setDraftDueDate(event.target.value)} disabled={saving} />
                </label>
              )}
              {onSaveAssignee && (
                <label className="min-w-0 grid gap-1.5 text-sm font-medium text-muted-foreground">
                  {t("projects:assignee")}
                  <Select value={draftAssignee || "__project-unassigned__"} onValueChange={(value) => setDraftAssignee(value === "__project-unassigned__" ? "" : value)} onOpenChange={(open) => { if (open) onAssigneePickerOpen?.(); }} disabled={saving}>
                    <SelectTrigger aria-label={t("projects:selectAssignee")}><SelectValue placeholder={t("projects:unassigned")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__project-unassigned__">{t("projects:unassigned")}</SelectItem>
                      {draftAssignee && !tenantUsers.some((user) => user.id === draftAssignee) && <SelectItem value={draftAssignee}>{draftAssignee}</SelectItem>}
                      {tenantUsers.map((user) => <SelectItem key={user.id} value={user.id}>{user.display_name ?? user.email ?? user.id}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </label>
              )}
            </div>
          )}
          {(!tagSupported || (onSaveTags && tags)) && (
            <div className="mt-3 flex items-center gap-2 text-sm">
              <span className="font-medium text-muted-foreground">{t("projects:tags")}</span>
              {!tagSupported ? <span className="text-muted-foreground">{t("projects:tagsUnavailable")}</span> : <Popover open={tagPopoverOpen} onOpenChange={setTagPopoverOpen}>
                <PopoverTrigger asChild><Button type="button" variant="outline" size="icon-sm" aria-label={t("projects:addTag")} disabled={saving}><TagIcon className="size-4" /></Button></PopoverTrigger>
                <PopoverContent align="start" className="w-72 p-2">
                  {onCreateTag && (
                    <>
                      <div className="flex gap-1">
                        <Input value={newTagName} onChange={(event) => setNewTagName(event.target.value)}
                          onKeyDown={(event) => {
                            event.stopPropagation();
                            if (event.key === "Enter") {
                              event.preventDefault();
                              void createTag();
                            }
                          }}
                          placeholder={t("projects:tagNamePlaceholder")}
                          aria-label={t("projects:createNewTag")}
                          disabled={creatingTag || saving || tagCreationUnavailable}
                          className="h-8 text-sm" />
                        <Button type="button" size="icon-sm" className="h-8 w-8 shrink-0"
                          aria-label={t("projects:createNewTag")}
                          disabled={!newTagName.trim() || creatingTag || saving || tagCreationUnavailable}
                          onClick={() => void createTag()}>
                          {creatingTag ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Plus aria-hidden="true" />}
                        </Button>
                      </div>
                      <div className="mt-2 flex items-center gap-1.5" aria-label={t("projects:tagColor")}>
                        <span className="mr-0.5 text-[11px] text-muted-foreground">{t("projects:tagColor")}</span>
                        <button type="button" className={cn("size-4 rounded-full border bg-muted", !newTagColor && "ring-2 ring-ring ring-offset-1")}
                          aria-label={t("projects:noTagColor")} title={t("projects:noTagColor")}
                          disabled={creatingTag || saving || tagCreationUnavailable} onClick={() => setNewTagColor(undefined)} />
                        {TAG_COLOR_OPTIONS.map((color) => (
                          <button key={color} type="button"
                            className={cn("size-4 rounded-full border", tagColorClass(color), newTagColor === color && "ring-2 ring-ring ring-offset-1")}
                            aria-label={t("projects:tagColorOption", { color: t(`projects:tagColors.${color}`) })}
                            title={t(`projects:tagColors.${color}`)}
                            disabled={creatingTag || saving || tagCreationUnavailable} onClick={() => setNewTagColor(color)} />
                        ))}
                      </div>
                    </>
                  )}
                  {tagCreateMessage && <p role="alert" className="px-2 py-1 text-xs text-destructive">{tagCreateMessage}</p>}
                  <div className="mt-2 max-h-52 overflow-y-auto border-t pt-1">
                    {tagPickerMessage && <p role="status" className="px-2 py-1 text-xs text-muted-foreground">{tagPickerMessage}</p>}
                    {selectableTags.map((tag) => {
                      const inherited = effectiveTags?.find((entry) => entry.tag.id === tag.id && entry.inherited);
                      const checked = draftTagIds.includes(tag.id);
                      return <Button key={tag.id} type="button" variant="ghost" size="sm" role="checkbox" aria-checked={checked}
                        className="h-8 w-full justify-start gap-2 px-2 text-sm font-normal" disabled={saving || Boolean(inherited)}
                        title={inherited ? t("projects:subtasks.inheritedTagReadOnly", { title: inherited.source_task?.title ?? t("projects:subtasks.parentUnavailable") }) : undefined}
                        onClick={() => setDraftTagIds((current) => checked ? current.filter((id) => id !== tag.id) : [...current, tag.id])}>
                        <span className="grid size-4 shrink-0 place-items-center rounded-sm border">{checked && <Check className="size-3" />}</span>
                        <span className={cn("truncate rounded border px-1.5 py-0.5 text-xs", tagColorClass(tag.color))} style={tagColorStyle(tag.color)}>{tag.name}</span>
                      </Button>;
                    })}
                  </div>
                </PopoverContent>
              </Popover>}
              <span className="flex flex-wrap gap-1">{draftTagIds.map((id) => {
                const tag = availableTags.find((item) => item.id === id) ?? tags?.find((item) => item.id === id);
                return tag ? <Badge key={id} variant="outline" className={tagColorClass(tag.color)} style={tagColorStyle(tag.color)}>{tag.name}</Badge> : null;
              })}</span>
            </div>
          )}
        </>
      ) : hasDescription ? (
        <>{taskTitle && <p className="mb-2 font-medium">{taskTitle}</p>}<MessageResponse>{description ?? ""}</MessageResponse></>
      ) : (
        <>{taskTitle && <p className="mb-2 font-medium">{taskTitle}</p>}<p className="text-muted-foreground">{t("taskBrief.noDescription")}</p></>
      )}
      {editing && (
        <>
          {saveError && <p role="alert" className="mt-3 text-sm text-destructive">{t("projects:taskUpdateFailed")}</p>}
          <div className="mt-3 flex justify-end gap-2">
            <Button type="button" variant="outline" disabled={saving || creatingTag} onClick={cancelEditing}>
              {t("taskBrief.cancel")}
            </Button>
            <Button type="button" disabled={saving || creatingTag || Boolean(onSaveTitle && !draftTitle.trim())} onClick={() => void saveTask()}>
              {t("taskBrief.save")}
            </Button>
          </div>
        </>
      )}
      {artifactSection}
    </div>
  );

  // Rendered whenever saving is wired so the header row keeps its width while
  // editing; the pencil is disabled rather than swapped out for the same reason.
  const editButton = onSaveTitle || onSaveDescription || onSaveDueDate || onSaveAssignee || onSaveTags ? (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-8 shrink-0"
      aria-label={t(taskTitle ? "taskBrief.editTask" : "taskBrief.edit")}
      disabled={editing || saving}
      onClick={startEditing}
    >
      <Pencil className="size-4" />
    </Button>
  ) : null;

  return (
    <Card
      asChild
      className={cn(
        "min-w-0 w-full text-left",
        className,
      )}
    >
      <section aria-label={t("taskBrief.title")} data-testid="task-brief-card">
        <Collapsible open={expanded} onOpenChange={setExpanded}>
          <div className="flex min-h-12 min-w-0 items-center gap-1 px-2 py-1.5">
            <CollapsibleTrigger
              className="min-w-0 flex-1 justify-start gap-2 px-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t(expanded ? "taskBrief.collapse" : "taskBrief.expand")}
            >
              <ChevronDown aria-hidden="true" className={cn(
                "size-4 shrink-0 text-muted-foreground transition-transform",
                !expanded && "-rotate-90",
              )} />
              <FileText aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate" title={expanded ? undefined : preview}>
                {expanded ? t("taskBrief.title") : preview || t("taskBrief.noDescription")}
              </span>
            </CollapsibleTrigger>
            {/* Independent buttons keep file actions out of the disclosure trigger. */}
            {!expanded && visibleArtifacts.length > 0 && (
              <div className="flex min-w-0 max-w-[45%] items-center gap-1.5">
                {visibleArtifacts.slice(0, 2).map((artifact) => artifactPill(artifact))}
              </div>
            )}
            {visibleArtifacts.length > 0 && (
              <span className="flex shrink-0 items-center gap-1 text-xs font-normal text-muted-foreground"
                title={t("taskBrief.fileCount", { count: visibleArtifacts.length })}>
                <Paperclip aria-hidden="true" className="size-3.5" />
                <span aria-hidden="true">{visibleArtifacts.length}</span>
                <span className="sr-only">{t("taskBrief.fileCount", { count: visibleArtifacts.length })}</span>
              </span>
            )}
            {dueDateSupported && isCalendarDate(dueDate) && (
              <time dateTime={dueDate} className={cn("shrink-0 rounded border px-1.5 py-0.5 text-xs", isTaskOverdue(dueDate, taskStatus, todayInTimezone(timezone)) ? "border-destructive/40 bg-destructive/10 text-destructive" : "border-border bg-muted text-foreground")}>
                {t(isTaskOverdue(dueDate, taskStatus, todayInTimezone(timezone)) ? "projects:overdueDate" : "projects:dueDateLabel", { date: formatTaskDueDate(dueDate, i18n.language) })}
              </time>
            )}
            {!dueDateSupported && <span className="text-xs text-muted-foreground">{t("projects:dueDatesUnavailable")}</span>}
            {headerActions}
            {editButton}
          </div>
          {/* The body swaps between the rendered description, the description
              editor, and the empty hint; the header above never moves. */}
          <CollapsibleContent>{body}</CollapsibleContent>
        </Collapsible>
      </section>
    </Card>
  );
}
